import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Sidebar } from "./Sidebar";
import { MobileNav } from "./MobileNav";
import { TopBar } from "./TopBar";
import { TOUCH_TARGETS } from "./navigation";

const SIDEBAR_KEY = "sidebar_open";

// Below lg (phones and portrait tablets) the sidebar would leave too little room
// for page content, so the app switches to the top-bar navigation sheet.
const COMPACT_QUERY = "(max-width: 1023px)";

function useCompactLayout() {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT_QUERY).matches);
  useEffect(() => {
    const mql = window.matchMedia(COMPACT_QUERY);
    const onChange = () => setCompact(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return compact;
}

export const AppLayout = () => {
  const [sidebarOpen, setSidebarOpenState] = useState(() => localStorage.getItem(SIDEBAR_KEY) !== "0");
  const isMobile = useCompactLayout();

  const setSidebarOpen = (open: boolean) => {
    localStorage.setItem(SIDEBAR_KEY, open ? "1" : "0");
    setSidebarOpenState(open);
  };

  return (
    <div className={cn("min-h-screen bg-sidebar", TOUCH_TARGETS)}>
      {!isMobile && <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />}

      <div
        className={cn(
          "min-h-screen bg-canvas transition-[margin] duration-300",
          !isMobile && "rounded-l-[28px]",
          isMobile ? "ml-0" : sidebarOpen ? "ml-[264px]" : "ml-[84px]"
        )}
      >
        <div className={cn("sticky top-0 z-90 bg-canvas/85 backdrop-blur-md px-4 sm:px-6 lg:px-8", !isMobile && "rounded-tl-[28px]")}>
          <TopBar leading={isMobile ? <MobileNav /> : undefined} />
        </div>
        <main className="px-4 sm:px-6 lg:px-8 pb-10 pt-2">
          <div className="mx-auto max-w-[1600px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
